import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	FormControl,
	FormLabel,
	Input,
	Snackbar,
	Switch,
	Table,
	Typography,
} from "@mui/joy";
import React from "react";
import { useRole } from "../Contexts/RoleContext";

interface AutomationConfig {
	[key: string]: string;
}

interface Automation {
	automationId: string;
	code: string;
	name: string;
	description?: string;
	enabled: boolean;
	scheduleType: string;
	scheduleValue: string;
	lastRunAt?: string;
	nextRunAt?: string;
	config: AutomationConfig;
}

interface AutomationRun {
	automation_run_id: string;
	automation_id: string;
	automation_code: string;
	automation_name: string;
	started_at: string;
	completed_at?: string;
	status: string;
	items_processed?: number;
	items_sent?: number;
	error_message?: string;
	duration_seconds?: number;
}

export default function AutomationsAdmin() {
	const { isActuallyAdmin } = useRole();
	const [automations, setAutomations] = React.useState<Automation[]>([]);
	const [runs, setRuns] = React.useState<AutomationRun[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [runningId, setRunningId] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [toast, setToast] = React.useState<string | null>(null);
	const [draftConfig, setDraftConfig] = React.useState<Record<string, Record<string, string>>>({});

	const loadAll = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [autoRes, runsRes] = await Promise.all([
				fetch("/Automation/GetAutomations", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }),
				fetch("/Automation/GetRuns", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ top: 200 }) }),
			]);

			const autoJson = await autoRes.json();
			const runsJson = await runsRes.json();

			if (autoJson.error) {
				setError(autoJson.error);
				return;
			}
			if (runsJson.error) {
				setError(runsJson.error);
				return;
			}

			const list = Array.isArray(autoJson) ? autoJson : [];
			setAutomations(list);
			setRuns(Array.isArray(runsJson) ? runsJson : []);

			const drafts: Record<string, Record<string, string>> = {};
			for (const a of list) {
				drafts[a.automationId] = { ...a.config };
			}
			setDraftConfig(drafts);
		} catch (e: any) {
			setError(e?.message ?? "Failed to load automations");
		} finally {
			setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		if (isActuallyAdmin()) loadAll();
	}, [isActuallyAdmin, loadAll]);

	const formatUtc = (value?: string | null) => {
		if (!value) return "—";
		const s = String(value).trim();
		const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
		return new Date(iso).toLocaleString(undefined, { timeZoneName: "short" });
	};

	const formatDuration = (seconds?: number) => {
		if (seconds == null) return "—";
		if (seconds < 60) return `${seconds}s`;
		return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
	};

	const scheduleLabel = (a: Automation) => {
		if (a.scheduleType === "interval_days") return `Every ${a.scheduleValue} days`;
		return `Daily at ${a.scheduleValue} UTC`;
	};

	const toggleEnabled = async (automation: Automation, enabled: boolean) => {
		const res = await fetch("/Automation/SetEnabled", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ automationId: automation.automationId, enabled }),
		});
		const json = await res.json();
		if (json.error) {
			setError(json.error);
			return;
		}
		setToast(enabled ? "Automation enabled" : "Automation disabled");
		await loadAll();
	};

	const saveConfig = async (automationId: string, configKey: string) => {
		const value = draftConfig[automationId]?.[configKey] ?? "";
		const res = await fetch("/Automation/UpdateConfig", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ automationId, configKey, configValue: value }),
		});
		const json = await res.json();
		if (json.error) {
			setError(json.error);
			return;
		}
		setToast(`Saved ${configKey}`);
		await loadAll();
	};

	const runNow = async (automationId: string) => {
		setRunningId(automationId);
		setError(null);
		try {
			const res = await fetch("/Automation/RunNow", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ automationId }),
			});
			const json = await res.json();
			if (json.error) {
				setError(json.error);
				return;
			}
			setToast("Automation run completed");
			await loadAll();
		} catch (e: any) {
			setError(e?.message ?? "Run failed");
		} finally {
			setRunningId(null);
		}
	};

	if (!isActuallyAdmin()) {
		return <Alert color="warning">Admin access required.</Alert>;
	}

	return (
		<Box sx={{ overflowY: "auto" }}>
			<Typography level="h3" sx={{ mb: 2 }}>
				Automations
			</Typography>
			<Typography level="body-sm" sx={{ mb: 2, color: "text.tertiary" }}>
				Hourly Azure Function runner executes jobs when next run time has passed.
			</Typography>

			{loading ? (
				<CircularProgress />
			) : (
				<>
					{automations.map((a) => (
						<Card key={a.automationId} sx={{ mb: 2 }}>
							<CardContent>
								<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
									<Typography level="title-lg">{a.name}</Typography>
									<Chip size="sm" variant="soft" color={a.enabled ? "success" : "neutral"}>
										{a.enabled ? "Enabled" : "Disabled"}
									</Chip>
									<Chip size="sm" variant="outlined">
										{a.code}
									</Chip>
								</Box>
								{a.description && (
									<Typography level="body-sm" sx={{ mb: 1 }}>
										{a.description}
									</Typography>
								)}
								<Typography level="body-sm">
									<strong>Schedule:</strong> {scheduleLabel(a)}
								</Typography>
								<Typography level="body-sm">
									<strong>Next run:</strong> {formatUtc(a.nextRunAt)}
								</Typography>
								<Typography level="body-sm" sx={{ mb: 1 }}>
									<strong>Last run:</strong> {formatUtc(a.lastRunAt)}
								</Typography>

								<Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
									<Switch
										checked={a.enabled}
										onChange={(e) => toggleEnabled(a, e.target.checked)}
										endDecorator={a.enabled ? "On" : "Off"}
									/>
									<Button
										size="sm"
										variant="soft"
										loading={runningId === a.automationId}
										onClick={() => runNow(a.automationId)}>
										Run now
									</Button>
								</Box>

								<Typography level="title-sm" sx={{ mb: 1 }}>
									Configuration
								</Typography>
								{Object.keys(a.config).length === 0 ? (
									<Typography level="body-sm" sx={{ color: "text.tertiary" }}>
										No config keys.
									</Typography>
								) : (
									Object.keys(a.config).map((key) => (
										<FormControl key={key} sx={{ mb: 1, maxWidth: 360 }}>
											<FormLabel>{key}</FormLabel>
											<Box sx={{ display: "flex", gap: 1 }}>
												<Input
													value={draftConfig[a.automationId]?.[key] ?? ""}
													onChange={(e) =>
														setDraftConfig((prev) => ({
															...prev,
															[a.automationId]: {
																...prev[a.automationId],
																[key]: e.target.value,
															},
														}))
													}
												/>
												<Button size="sm" onClick={() => saveConfig(a.automationId, key)}>
													Save
												</Button>
											</Box>
										</FormControl>
									))
								)}
							</CardContent>
						</Card>
					))}

					<Typography level="title-md" sx={{ mb: 1, mt: 2 }}>
						Run history
					</Typography>
					<Table stripe="even" hoverRow>
						<thead>
							<tr>
								<th>Started</th>
								<th>Automation</th>
								<th>Status</th>
								<th>Processed</th>
								<th>Sent</th>
								<th>Duration</th>
								<th>Error</th>
							</tr>
						</thead>
						<tbody>
							{runs.length === 0 ? (
								<tr>
									<td colSpan={7} style={{ textAlign: "center", padding: "1rem" }}>
										No runs logged yet. Deploy SQL scripts and trigger a run.
									</td>
								</tr>
							) : (
								runs.map((r) => (
									<tr key={r.automation_run_id}>
										<td>{formatUtc(r.started_at)}</td>
										<td>{r.automation_name}</td>
										<td>
											<Chip
												size="sm"
												color={r.status === "Success" ? "success" : r.status === "Failed" ? "danger" : "warning"}
												variant="soft">
												{r.status}
											</Chip>
										</td>
										<td>{r.items_processed ?? "—"}</td>
										<td>{r.items_sent ?? "—"}</td>
										<td>{formatDuration(r.duration_seconds)}</td>
										<td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{r.error_message ?? ""}
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</>
			)}

			<Snackbar open={!!error} color="danger" onClose={() => setError(null)}>
				{error}
			</Snackbar>
			<Snackbar open={!!toast} color="success" onClose={() => setToast(null)}>
				{toast}
			</Snackbar>
		</Box>
	);
}
